<script setup lang="ts">
import { onMounted, reactive, ref } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import type { BillRow } from '@/api/types/domain'
import { deleteBill, fetchBills } from '@/api/modules/bills'
import { formatEpoch, formatMoneyInt } from '@/utils/format'

const q = reactive({ keyword: '', page: 1, rows: 15 })
const loading = ref(false)
const content = ref<BillRow[]>([])
const total = ref(0)

async function reload() {
  loading.value = true
  try {
    const { data } = await fetchBills(q)
    content.value = data.content ?? []
    total.value = data.totalElements ?? 0
  } finally {
    loading.value = false
  }
}

onMounted(reload)

function search() {
  q.page = 1
  reload()
}

async function onDel(id: number) {
  await ElMessageBox.confirm('确认删除?', '提示', { type: 'warning' })
  await deleteBill(id)
  ElMessage.success('已删除')
  reload()
}

function onSize(sz: number) {
  q.rows = sz
  q.page = 1
  reload()
}
function onPage() {
  reload()
}
</script>

<template>
  <PageChrome title="账单管理" description="对齐 /api/admin/backoffice/bills；按单号关键字分页检索。">
    <template #actions>
      <el-button :loading="loading" @click="reload">刷新</el-button>
    </template>

    <div class="ep-filter-panel">
      <el-form inline label-width="100px">
        <el-form-item label="单号关键字">
          <el-input v-model="q.keyword" clearable placeholder="支持模糊" style="width: 220px" />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="search">检索</el-button>
        </el-form-item>
      </el-form>
    </div>

    <el-card shadow="hover" class="ep-table-scope">
      <el-table v-loading="loading" :data="content" stripe border empty-text="无数据">
        <el-table-column prop="id" label="ID" width="76" align="center" />
        <el-table-column prop="user" label="用户" width="132" />
        <el-table-column prop="ordernum" label="关联单号" min-width="160" show-overflow-tooltip />
        <el-table-column label="金额" align="right">
          <template #default="{ row }">{{ formatMoneyInt(row.money) }}</template>
        </el-table-column>
        <el-table-column label="时间" width="176">
          <template #default="{ row }">{{ formatEpoch(row.addtime ?? undefined) }}</template>
        </el-table-column>
        <el-table-column fixed="right" label="操作" width="100" align="center">
          <template #default="{ row }">
            <el-button link type="danger" @click="onDel(row.id)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
      <div class="ep-pager-footer">
        <el-pagination
          v-model:current-page="q.page"
          v-model:page-size="q.rows"
          :total="total"
          :page-sizes="[10, 15, 25]"
          layout="sizes,prev,pager,next,jumper,total"
          background
          @current-change="onPage"
          @size-change="onSize"
        />
      </div>
    </el-card>
  </PageChrome>
</template>
